import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { checkLoginStatus, fetchBinaryTree } from '../redux/WithcookieSlice';
import { useTranslation } from '../redux/TranslationHelper';
import './BinaryLegsComponent.css';

// 조직도 노드 컴포넌트
const OrganizationChartNode = ({ node, onClick, isRootNode = false, titleStyle = {}, shipperTitleStyle = {}, lineColor = '#3f51b5' }) => {
  const { t } = useTranslation();
  
  if (!node) return null;
  
  // 자식 노드 확인
  const hasLeftChild = node.children && node.children.length > 0 && node.children[0];
  const hasRightChild = node.children && node.children.length > 1 && node.children[1];
  const hasAnyChild = hasLeftChild || hasRightChild;
  const hasSingleChild = (hasLeftChild && !hasRightChild) || (!hasLeftChild && hasRightChild);
  
  // 자식이 하나일 때 사용할 실제 자식 노드
  const singleChild = hasLeftChild ? node.children[0] : (hasRightChild ? node.children[1] : null);
  
  // node.class에 따라 타이틀 스타일 선택
  const nodeSpecificTitleStyle = node.class && node.class.includes('shipper') ? shipperTitleStyle : titleStyle;
  
  // 선 스타일 설정
  const lineStyle = {
    backgroundColor: lineColor
  };
  
  // title1과 title2를 처리하는 함수
  const processTitle = (title) => {
    if (!title) return '';
    
    // "성함:" 또는 "센터:" 부분을 번역된 텍스트로 대체
    let processedTitle = title;
    
    // "성함:" 문자열 처리
    if (processedTitle.includes('성함:')) {
      processedTitle = processedTitle.replace('성함:', `${t('성함')}:`);
    }
    
    // "센터:" 문자열 처리
    if (processedTitle.includes('센터:')) {
      processedTitle = processedTitle.replace('센터:', `${t('센터')}:`);
    }
    
    return processedTitle;
  };
  
  return (
    <div className="node-wrapper">
      <table cellSpacing="0" cellPadding="0">
        <tbody>
          <tr>
            <td colSpan="2" align="center">
              <div className={`node ${node.class || ''} ${isRootNode ? 'root-node' : ''}`} onClick={() => onClick(node, isRootNode)}>
                <div className="title" style={nodeSpecificTitleStyle}>
                  {node.name}
                </div>
                <div className="content" style={{fontSize: '12px', color: '#444 !important'}}>
                  <div>{processTitle(node.title1)}</div>
                  {node.title2 && node.title2 !== '-' && <div>{processTitle(node.title2)}</div>}
                </div>
              </div>
            </td>
          </tr>
          {hasAnyChild && (
            <>
              {/* 부모 노드에서 내려오는 세로선 */}
              <tr className="lines">
                <td colSpan="2" align="center" style={{ height: '25px', padding: 0 }}>
                  <div className="downLine" style={lineStyle}></div>
                </td>
              </tr>
              
              {/* 자식이 하나인 경우와 여러 개인 경우 분기 */}
              {hasSingleChild ? (
                // 자식이 하나인 경우 - 세로선만 표시
                <tr className="nodes">
                  <td colSpan="2" align="center" valign="top" className="single-child-container">
                    {singleChild && (
                      <OrganizationChartNode 
                        node={singleChild} 
                        onClick={onClick} 
                        isRootNode={false}
                        titleStyle={titleStyle}
                        shipperTitleStyle={shipperTitleStyle}
                        lineColor={lineColor}
                      />
                    )}
                  </td>
                </tr>
              ) : (
                // 자식이 두 개인 경우 - 가로선과 각각의 노드 표시
                <>
                  {/* 가로선과 세로선을 하나의 tr로 통합 */}
                  <tr className="lines">
                    <td className="rightLine" style={{ height: '25px', padding: 0 }}>
                      {hasLeftChild && <div className="child-connector" style={lineStyle}></div>}
                    </td>
                    <td className="leftLine" style={{ height: '25px', padding: 0 }}>
                      {hasRightChild && <div className="child-connector" style={lineStyle}></div>}
                    </td>
                  </tr>
                  <tr className="nodes">
                    <td align="center" valign="top" style={{ padding: '0 25px' }}>
                      {hasLeftChild && (
                        <OrganizationChartNode 
                          node={node.children[0]} 
                          onClick={onClick} 
                          isRootNode={false}
                          titleStyle={titleStyle}
                          shipperTitleStyle={shipperTitleStyle}
                          lineColor={lineColor}
                        />
                      )}
                    </td>
                    <td align="center" valign="top" style={{ padding: '0 25px' }}>
                      {hasRightChild && (
                        <OrganizationChartNode 
                          node={node.children[1]} 
                          onClick={onClick} 
                          isRootNode={false}
                          titleStyle={titleStyle}
                          shipperTitleStyle={shipperTitleStyle}
                          lineColor={lineColor}
                        />
                      )}
                    </td>
                  </tr>
                </>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

// 메인 바이너리 레그 컴포넌트
const BinaryLegsComponent = ({ id, componentProps = {}, style = {} }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.withcookie.user);
  const binaryTreeData = useSelector(state => state.withcookie.binaryTreeData);
  const binaryTreeLoading = useSelector(state => state.withcookie.binaryTreeLoading);
  const binaryTreeError = useSelector(state => state.withcookie.binaryTreeError);
  
  // withcookieData에서 테마 색상 가져오기
  const withcookieData = useSelector(state => state.withcookie.withcookieData);
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#3f51b5";
  const secondaryColor = colorSet?.secondary || "#e91e63";
  
  // 노드 타이틀 스타일 설정
  const nodeTitleStyle = {
    backgroundColor: primaryColor
  };
  
  // 특별한 노드(shipper) 타이틀 스타일
  const shipperTitleStyle = {
    backgroundColor: secondaryColor
  };
  
  // 버튼 스타일 설정
  const buttonStyle = {
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '5px 15px',
    marginRight: '10px',
    marginBottom: '10px',
    cursor: 'pointer',
    fontSize: '14px'
  };
  
  // 비활성화된 버튼 스타일
  const disabledButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
    opacity: 0.7
  };
  
  const [currentUserId, setCurrentUserId] = useState(null);
  const [debugMode, setDebugMode] = useState(true);
  const [mockingEnabled, setMockingEnabled] = useState(false);
  const [customApiUrl, setCustomApiUrl] = useState('');
  const [nodeHistory, setNodeHistory] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const minZoom = 0.3;
  const maxZoom = 2.5;
  const lastTouchDist = React.useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const lastPos = React.useRef({ x: 0, y: 0 });

  // 임시 모킹 데이터 생성 (API가 동작하지 않을 때 테스트용)
  const generateMockData = useCallback((rootUserId) => {
    const rootId = rootUserId || (user?.id || 1);
    return {
      id: rootId,
      name: `${t('사용자')} ${rootId}`,
      title1: `${t('성함')}: ${user?.name || `${t('테스트 사용자')} ${rootId}`}`,
      title2: `${t('센터')}: ${t('테스트 센터')}`,
      class: 'shipper',
      children: [
        {
          id: rootId + 1,
          name: `${t('사용자')} ${rootId + 1}`,
          title1: `${t('성함')}: ${t('테스트 사용자')} ${rootId + 1}`,
          title2: `${t('센터')}: ${t('왼쪽 센터')}`,
          class: '',
          children: []
        },
        {
          id: rootId + 2,
          name: `${t('사용자')} ${rootId + 2}`,
          title1: `${t('성함')}: ${t('테스트 사용자')} ${rootId + 2}`,
          title2: `${t('센터')}: ${t('오른쪽 센터')}`,
          class: '',
          children: []
        }
      ]
    };
  }, [user, t]);

  // 노드 클릭 핸들러
  const handleNodeClick = useCallback(async (nodeData, isRootNode = false) => {
    
    // 현재 노드를 히스토리에 추가
    setNodeHistory(prev => [...prev, currentUserId]);
    
    setCurrentUserId(nodeData.id);
    
    // 최상단 노드 클릭 시 one_upper 파라미터 추가
    
    // API 파라미터 준비
    const apiParams = {
      id: String(nodeData.id),
      depth: "8"
    };
    
    // isRootNode 확인 - 직접 확인
    
    // 최상단 노드 클릭 확인을 단순화하고 명확하게 확인
    // isRootNode가 true이거나 현재 표시된 최상위 노드와 ID가 같은 경우
    const isTopLevelNode = isRootNode || 
                          (binaryTreeData && String(binaryTreeData.id) === String(nodeData.id));
    
    
    if (isTopLevelNode) {
      apiParams.one_upper = true;
    }
    
    // API 호출 - 객체 직접 전달
    dispatch(fetchBinaryTree({...apiParams}));
    
   
  }, [dispatch, currentUserId, binaryTreeData, binaryTreeLoading, binaryTreeError]);

  // 이전 노드로 돌아가는 함수
  const goToPreviousNode = useCallback(() => {
    if (nodeHistory.length > 0) {
      // 히스토리에서 마지막 노드 ID 가져오기
      const prevNodeId = nodeHistory[nodeHistory.length - 1];
      
      // 히스토리에서 마지막 항목 제거
      setNodeHistory(prev => prev.slice(0, -1));
      
      // 이전 노드로 이동
      setCurrentUserId(prevNodeId);
      
      // 이전 노드가 현재 사용자의 루트 노드인지 확인
      const isUserRootNode = user && user.id && String(prevNodeId) === String(user.id);
      
      dispatch(fetchBinaryTree({
        id: String(prevNodeId),
        depth: "8",
        // 사용자의 루트 노드로 이동하는 경우에는 one_upper를 true로 설정
        ...(isUserRootNode && { one_upper: true })
      }));
    }
  }, [nodeHistory, dispatch, user]);

  // 루트 노드로 돌아가는 함수
  const goToRootNode = useCallback(() => {
    if (user && user.id) {
      // 히스토리 초기화
      setNodeHistory([]);
      
      // 루트 노드(로그인한 사용자)로 이동
      setCurrentUserId(user.id);
      dispatch(fetchBinaryTree({
        id: String(user.id),
        depth: "8",
        // 루트 노드로 이동할 때 one_upper를 true로 설정
        one_upper: true
      }));
    }
  }, [user, dispatch]);

  // 초기 데이터 로드
  useEffect(() => {
    const checkAuth = async () => {
      // 사용자 인증 상태 확인
      await dispatch(checkLoginStatus({ forceRefresh: true }));
    };
    
    checkAuth();
  }, [dispatch]);

  // 사용자 정보가 로드되면 해당 사용자 ID로 바이너리 트리 로드
  useEffect(() => {
    if (user) {
      setCurrentUserId(user.id);
      
      // 히스토리 초기화
      setNodeHistory([]);
      
      // 이미 트리 데이터가 있거나 로딩 중이 아닌 경우에만 요청
      if (!binaryTreeData && !binaryTreeLoading) {
        dispatch(fetchBinaryTree({ id: String(user.id) }));
      }
    }
  }, [user, dispatch]);

  // 모킹 모드 전환 시 데이터 업데이트
  const enableMockData = useCallback(() => {
    setMockingEnabled(true);
  }, []);

  // 임시 데이터 로드
  useEffect(() => {
    if (mockingEnabled && user) {
      const mockData = generateMockData(user.id);
    }
  }, [mockingEnabled, user, generateMockData]);

  // 실제 API로 다시 시도
  const retryWithRealApi = useCallback(() => {
    if (user && user.id) {
      setMockingEnabled(false);
      dispatch(fetchBinaryTree({ id: String(user.id) }));
    }
  }, [user, dispatch]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    let delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoomLevel(z => Math.min(maxZoom, Math.max(minZoom, z + delta)));
  }, []);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(dx * dx + dy * dy);
    }
  };
  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && lastTouchDist.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const newDist = Math.sqrt(dx * dx + dy * dy);
      const diff = newDist - lastTouchDist.current;
      if (Math.abs(diff) > 5) {
        setZoomLevel(z => {
          let next = z + (diff > 0 ? 0.05 : -0.05);
          return Math.min(maxZoom, Math.max(minZoom, next));
        });
        lastTouchDist.current = newDist;
      }
    }
  };
  const handleTouchEnd = () => {
    lastTouchDist.current = null;
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    lastPos.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    lastPos.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStartPan = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };
  const handleTouchMovePan = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - lastPos.current.x;
    const dy = e.touches[0].clientY - lastPos.current.y;
    setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const handleTouchEndPan = () => {
    setIsDragging(false);
  };

  return (
    <div className="binary-legs-component" style={style}>
      <h2 className="mb-4 d-flex">
        {t(componentProps.title) || t("후원 레그")}
      </h2>
      
      {/* 네비게이션 버튼 추가 */}
      <div className="binary-navigation-controls">
        <button 
          onClick={goToPreviousNode} 
          disabled={nodeHistory.length === 0}
          className="binary-nav-button"
          title={t("이전 노드로 돌아가기")}
          style={nodeHistory.length === 0 ? disabledButtonStyle : buttonStyle}
        >
          ← {t("이전으로")}
        </button>
        
        <button 
          onClick={goToRootNode} 
          disabled={currentUserId === user?.id}
          className="binary-nav-button"
          title={t("내 레그로 돌아가기")}
          style={currentUserId === user?.id ? disabledButtonStyle : buttonStyle}
        >
          {t("처음으로")}
        </button>
        
        {currentUserId && currentUserId !== user?.id && (
          <span className="current-viewing-node" style={{color: '#222', fontSize: '12px'}}>
            {t("현재 보는 중")}: {binaryTreeData?.name || `ID: ${currentUserId}`}
            {binaryTreeData?.title1 ? ` (${binaryTreeData.title1.replace(t('성함')+': ', '')})` : ''}
          </span>
        )}
      </div>
      
      {currentUserId && currentUserId !== user?.id && (
        <div className="navigation-breadcrumb">
          <span className="breadcrumb-item root" onClick={goToRootNode}>{t("내 레그")}</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item current">
            {binaryTreeData?.name || `ID: ${currentUserId}`}
          </span>
        </div>
      )}
      
      <div
        className="orgchart-container"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={(e) => { handleTouchStart(e); handleTouchStartPan(e); }}
        onTouchMove={(e) => { handleTouchMove(e); handleTouchMovePan(e); }}
        onTouchEnd={(e) => { handleTouchEnd(); handleTouchEndPan(e); }}
        style={{
          overflow: 'auto',
          touchAction: 'none',
          WebkitOverflowScrolling: 'touch',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        <div
          className="orgchart"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoomLevel})`,
            transformOrigin: '50% 0',
            transition: isDragging ? 'none' : 'transform 0.1s',
            display: 'inline-block',
          }}
        >
          {binaryTreeLoading && <div className="loading-indicator">{t("로딩중...")}</div>}
          {binaryTreeError && (
            <div className="error-message">
              <p>{binaryTreeError}</p>
              {debugMode && (
                <div className="debug-info" style={{backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px', marginTop: '10px', fontSize: '12px', textAlign: 'left'}}>
                  <h4>{t("디버그 정보")}</h4>
                  <p>{t("현재 위치")}: {window.location.href}</p>
                  <p>{t("호스트")}: {window.location.hostname}</p>
                  <p>{t("사용자 ID")}: {currentUserId}</p>
                  {user && (
                    <div>
                      <h5>{t("로그인 사용자 정보")}</h5>
                      <p>{t("이름")}: {user.name} (ID: {user.id})</p>
                      <p>{t("회원유형")}: {user.role}</p>
                      <p>binaryPosition: {user.binaryPosition || t('없음')}</p>
                      <p>{t("추천인코드")}: {user.referralCode || t('없음')}</p>
                    </div>
                  )}
                  <p>{t("문제 해결 방법")}:</p>
                  <ol>
                    <li>{t("개발자 도구(F12)의 콘솔 및 네트워크 탭에서 API 호출 오류를 확인하세요.")}</li>
                    <li>{t("404 오류는 API 엔드포인트가 존재하지 않는다는 의미입니다. 백엔드 개발자에게 정확한 API 경로를 확인하세요.")}</li>
                  </ol>
                  
                  <h5>{t("직접 테스트하기")}</h5>
                  <div style={{marginBottom: '10px'}}>
                    <label style={{display: 'block', marginBottom: '5px'}}>
                      <input 
                        type="checkbox" 
                        checked={mockingEnabled}
                        onChange={(e) => setMockingEnabled(e.target.checked)}
                      /> 
                      {t("임시 모킹 데이터 사용 (API 대신 테스트 데이터 사용)")}
                    </label>
                  </div>
                  
                  {!mockingEnabled && (
                    <div>
                      <div style={{marginBottom: '10px'}}>
                        <label style={{display: 'block', marginBottom: '5px'}}>{t("API 호스트")}:</label>
                        <input 
                          type="text" 
                          value={customApiUrl} 
                          onChange={(e) => setCustomApiUrl(e.target.value)}
                          placeholder={t("예: https://nesem.io.kr")}
                          style={{width: '100%', padding: '5px'}}
                        />
                      </div>
                      <button 
                        onClick={retryWithRealApi}
                        style={{padding: '5px 10px', backgroundColor: primaryColor, color: 'white', border: 'none', borderRadius: '4px'}}
                      >
                        {t("API 다시 시도")}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {!binaryTreeLoading && !binaryTreeError && binaryTreeData && (
            <OrganizationChartNode 
              node={mockingEnabled ? generateMockData(currentUserId) : binaryTreeData} 
              onClick={handleNodeClick}
              isRootNode={true}
              titleStyle={nodeTitleStyle}
              shipperTitleStyle={shipperTitleStyle}
              lineColor={primaryColor}
            />
          )}
          {!binaryTreeLoading && !binaryTreeError && !binaryTreeData && (
            <div className="empty-message">
              {user ? t("바이너리 레그 정보가 없습니다.") : t("로그인이 필요합니다.")}
              {debugMode && user && (
                <div style={{marginTop: '15px', fontSize: '12px', color: '#666'}}>
                  <p>{t("사용자 정보는 있지만 바이너리 레그 데이터를 가져오지 못했습니다.")}</p>
                  <p>{t("콘솔에서 API 요청 로그를 확인하세요.")}</p>
                  
                  <div style={{marginTop: '15px'}}>
                    <button 
                      onClick={enableMockData}
                      style={{padding: '5px 10px', backgroundColor: secondaryColor, color: 'white', border: 'none', borderRadius: '4px', marginRight: '10px'}}
                    >
                      {t("임시 데이터로 테스트")}
                    </button>
                    <button 
                      onClick={retryWithRealApi}
                      style={{padding: '5px 10px', backgroundColor: primaryColor, color: 'white', border: 'none', borderRadius: '4px'}}
                    >
                      {t("API 다시 시도")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BinaryLegsComponent; 